import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileImage, Ruler, Scan, Boxes, DoorOpen, LayoutGrid, Download, Loader2, Layers } from 'lucide-react';
import StepRail from '../canvas/StepRail';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const opts = { withCredentials: true };

/** Étapes de l'éditeur de lot — V17.1 (Mobilier déplacé dans Génération › Meubler). */
export const LOT_STEPS = [
  { id: 'preparation', n: 1, label: 'Plan source', icon: FileImage, subs: [
    { id: 'documents', label: 'Document & recadrage', icon: FileImage, done: (s) => !!s?.source_file_id },
    { id: 'scale', label: 'Mise à l\u2019échelle', icon: Ruler, done: (s) => !!s?.structure?.calibration?.scale_m_per_norm_unit },
  ] },
  { id: 'delimitation', n: 2, label: 'Délimitation', icon: Scan, tab: 'structure', sub: 'contour', subs: [
    { id: 'contour', label: 'Contour du lot', icon: Scan, done: (s) => (s?.structure?.outer_polygon || []).length >= 3 },
  ] },
  { id: 'structure', n: 3, label: 'Structure', icon: Boxes, subs: [
    { id: 'walls', label: 'Murs · escaliers · éléments fixes', icon: Boxes, done: (s) => (s?.structure?.walls || []).length > 0 },
    { id: 'openings', label: 'Menuiseries', icon: DoorOpen, done: (s) => (s?.structure?.openings || []).length > 0 },
  ] },
  { id: 'pieces', n: 4, label: 'Pièces & matériaux', icon: LayoutGrid, subs: [
    { id: 'rooms', label: 'Nommage & matériaux', icon: LayoutGrid, done: (s) => (s?.structure?.rooms || []).length > 0 },
  ] },
];

const stepOf = (tab, sub) => (tab === 'structure' && sub === 'contour' ? 'delimitation' : tab);

/** Bloc « Récupérer depuis l'étage » (étape 2). */
const FloorImport = ({ projectId, lotId, onImported }) => {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { let ok = true; setInfo(null); axios.get(`${API}/projects/${projectId}/plan-source/floor-emprise`, { ...opts, params: { lot_id: lotId } }).then((r) => ok && setInfo(r.data)).catch(() => ok && setInfo({ available: false, reason: 'Indisponible' })); return () => { ok = false; }; }, [projectId, lotId]);
  const run = async () => {
    setBusy(true);
    try { const r = await axios.post(`${API}/projects/${projectId}/plan-source/import-floor-emprise`, null, { ...opts, params: { lot_id: lotId } }); toast.success(`Contour posé (${r.data.area_m2} m²) · échelle : ${r.data.scale_source}`); onImported?.(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Import impossible'); } finally { setBusy(false); }
  };
  if (!info) return <div className="text-[10.5px] text-[var(--c-text-faint)] pl-7"><Loader2 className="w-3 h-3 inline animate-spin mr-1" />Emprise étage…</div>;
  return (
    <div className="ml-7 mr-2 mt-1 rounded-lg border border-[var(--c-border)] bg-[var(--c-surface-1)] p-2 space-y-1.5" data-testid="rail-floor-import">
      {info.available ? (
        <>
          <p className="text-[10.5px] text-[var(--c-text-2)] leading-snug"><Layers className="w-3 h-3 inline mr-1 text-[var(--c-gold)]" />Emprise tracée sur <b>{info.floor_name}</b> · {info.area_m2} m² · {info.width_m} × {info.height_m} m</p>
          <button type="button" onClick={run} disabled={busy} className="hud-btn hud-btn-primary hud-btn-sm w-full justify-center" data-testid="rail-floor-import-btn">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}Récupérer depuis l'étage</button>
        </>
      ) : <p className="text-[10.5px] text-[var(--c-text-faint)] leading-snug">{info.reason || 'Aucune emprise de lot sur l\u2019étage.'}</p>}
    </div>
  );
};

/** LotStepRail — rail vertical d'étapes (libre, pastilles d'avancement, sous-menus dépliables). */
export default function LotStepRail({ step, sub, onNavigate, savedState, projectId, lotId, onImported }) {
  const active = stepOf(step, sub);
  const steps = LOT_STEPS.map((st) => ({
    ...st,
    subs: st.subs.map((s) => ({ ...s, done: s.done(savedState) })),
    extra: st.id === 'delimitation' && lotId ? <FloorImport projectId={projectId} lotId={lotId} onImported={onImported} /> : null,
  }));
  return <StepRail steps={steps} active={active} activeSub={sub} onSelect={(st, s) => onNavigate?.(st.tab || st.id, s?.id || st.sub || st.subs[0]?.id || null)} testId="lot-step-rail" />;
}
